import { Routes } from '@angular/router';
import { authGuard } from './core/guards/auth.guard';
import { LayoutComponent } from './shared/components/layout/layout.component';

export const routes: Routes = [
  // Anonymous login route (no auth required)
  {
    path: '',
    loadComponent: () => import('./features/auth/anonymous-login/anonymous-login.component').then(m => m.AnonymousLoginComponent),
  },
  // Member login route (no auth required)
  {
    path: 'memberlogin',
    loadComponent: () => import('./features/auth/member-login/member-login.component').then(m => m.MemberLoginComponent),
  },
  // Protected routes with layout
  {
    path: '',
    component: LayoutComponent,
    canActivate: [authGuard],
    children: [
      {
        path: '',
        redirectTo: 'home',
        pathMatch: 'full',
      },
      {
        path: 'home',
        loadChildren: () => import('./features/home/home.routes').then(m => m.homeRoutes),
      },
      // Member/Shopper routes
      {
        path: 'shop',
        loadComponent: () => import('./features/shop/shop.component').then(m => m.ShopComponent),
      },
      {
        path: 'cart',
        loadComponent: () => import('./features/cart/cart.component').then(m => m.CartComponent),
      },
      {
        path: 'products',
        loadComponent: () => import('./features/products/products.component').then(m => m.ProductsComponent),
      },
      {
        path: 'products/detail/:productId',
        loadComponent: () => import('./features/product-detail/product-detail.component').then(m => m.ProductDetailComponent),
      },
      {
        path: 'profile-hub',
        loadComponent: () => import('./features/profile-hub/profile-hub.component').then(m => m.ProfileHubComponent),
      },
      {
        path: 'checkout',
        loadComponent: () => import('./features/checkout/checkout.component').then(m => m.CheckoutComponent),
      },
      {
        path: 'order-history',
        loadComponent: () => import('./features/order-history/order-history.component').then(m => m.OrderHistoryComponent),
      },
      // Manufacturer routes
      {
        path: 'manufacturer',
        children: [
          {
            path: 'dashboard',
            loadComponent: () => import('./features/manufacturer/dashboard/manufacturer-dashboard.component').then(m => m.ManufacturerDashboardComponent),
          },
          {
            path: 'users',
            loadComponent: () => import('./features/manufacturer/users/manufacturer-users.component').then(m => m.ManufacturerUsersComponent),
          },
          {
            path: 'products',
            loadComponent: () => import('./features/manufacturer/products/product-list.component').then(m => m.ProductListComponent),
          },
          {
            path: 'products/categories',
            loadComponent: () => import('./features/manufacturer/products/category-list.component').then(m => m.CategoryListComponent),
          },
          {
            path: 'products/types',
            loadComponent: () => import('./features/manufacturer/products/type-list.component').then(m => m.TypeListComponent),
          },
          {
            path: 'products/configuration',
            loadComponent: () => import('./features/manufacturer/products/configuration.component').then(m => m.ConfigurationComponent),
          },
          {
            path: 'products/:productId/variants',
            loadComponent: () => import('./features/manufacturer/products/variant-management.component').then(m => m.VariantManagementComponent),
          },
          {
            path: 'products/:productId/edit',
            loadComponent: () => import('./features/manufacturer/products/product-detail.component').then(m => m.ManufacturerProductDetailComponent),
          },
          {
            path: 'products/:productId',
            loadComponent: () => import('./features/manufacturer/products/product-detail.component').then(m => m.ManufacturerProductDetailComponent),
          },
          {
            path: 'embroidery',
            loadComponent: () => import('./features/manufacturer/embroidery/embroidery-dashboard.component').then(m => m.EmbroideryDashboardComponent),
          },
          {
            path: 'embroidery/locations',
            loadComponent: () => import('./features/manufacturer/embroidery/locations.component').then(m => m.EmbroideryLocationsComponent),
          },
          {
            path: 'embroidery/pricing',
            loadComponent: () => import('./features/manufacturer/embroidery/pricing.component').then(m => m.EmbroideryPricingComponent),
          },
          {
            path: 'embroidery/threads',
            loadComponent: () => import('./features/manufacturer/embroidery/thread-colors.component').then(m => m.ThreadColorsComponent),
          },
          {
            path: 'embroidery/fonts',
            loadComponent: () => import('./features/manufacturer/embroidery/fonts.component').then(m => m.EmbroideryFontsComponent),
          },
          {
            path: 'embroidery/settings',
            loadComponent: () => import('./features/manufacturer/embroidery/settings.component').then(m => m.EmbroiderySettingsComponent),
          },
          {
            path: 'inventory',
            loadComponent: () => import('./features/manufacturer/inventory/inventory.component').then(m => m.InventoryComponent),
          },
          {
            path: 'orders',
            loadComponent: () => import('./features/manufacturer/orders/orders.component').then(m => m.ManufacturerOrdersComponent),
          },
          {
            path: 'branding',
            loadComponent: () => import('./features/manufacturer/branding/branding.component').then(m => m.ManufacturerBrandingComponent),
          },
        ],
      },
      // Distributor routes
      {
        path: 'distributor',
        children: [
          { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
          {
            path: 'dashboard',
            loadComponent: () => import('./features/distributor/dashboard/distributor-dashboard.component').then(m => m.DistributorDashboardComponent),
          },
          {
            path: 'users',
            loadComponent: () => import('./features/distributor/users/distributor-users.component').then(m => m.DistributorUsersComponent),
          },
          {
            path: 'members',
            loadComponent: () => import('./features/distributor/members/members.component').then(m => m.MembersComponent),
          },
          {
            path: 'manufacturers',
            loadComponent: () => import('./features/distributor/manufacturers/manufacturers.component').then(m => m.ManufacturersComponent),
          },
          {
            path: 'products',
            loadComponent: () => import('./features/distributor/products/distributor-products.component').then(m => m.DistributorProductsComponent),
          },
          {
            path: 'products/:manufacturerId/:productId',
            loadComponent: () => import('./features/distributor/products/distributor-product-detail.component').then(m => m.DistributorProductDetailComponent),
          },
          {
            path: 'orders',
            loadComponent: () => import('./features/distributor/orders/distributor-orders.component').then(m => m.DistributorOrdersComponent),
          },
          {
            path: 'settings',
            loadComponent: () => import('./features/distributor/settings/settings.component').then(m => m.DistributorSettingsComponent),
          },
          {
            path: 'branding',
            loadComponent: () => import('./features/distributor/branding/branding.component').then(m => m.DistributorBrandingComponent),
          },
        ],
      },
    ],
  },
  {
    path: 'unauthorized',
    loadComponent: () => import('./shared/components/unauthorized/unauthorized.component').then(m => m.UnauthorizedComponent),
  },
  {
    path: '**',
    redirectTo: 'home',
  },
];
